import React, { cloneElement, SetStateAction, useEffect } from "react";
import * as THREE from "three";
import * as WindowResize from "./WindowResize";
import * as ObjectLib from "./ObjectLib";
import * as TextureLib from "./TextureLib";
import { getMusic, MusicService } from "./MusicLib";
import Toast from "./Toast";
import "../css/Trigger";
import "../css/Loading"

interface SimulatorProps {
  fps: number;
}

interface Viewport {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  clock: THREE.Clock;
}

interface SceneConfiguration {
  environmentMap: (scene: THREE.Scene) => Promise<void>;
  mesh: (scene: THREE.Scene) => Promise<void>;
  ObjectOfInterestName: string;
  ObjectOfInterestScale: number;
  BackgroundMusic: string;
}

const PassageWay: SceneConfiguration = {
  environmentMap: (scene: THREE.Scene) => TextureLib.GlassPassage(scene),
  mesh: (scene: THREE.Scene) => ObjectLib.StoneFruit(scene),
  ObjectOfInterestName: "StoneFruit",
  ObjectOfInterestScale: 3,
  BackgroundMusic: "Dhinchak",
};

const RollerCoaster: SceneConfiguration = {
  environmentMap: (scene: THREE.Scene) => TextureLib.Snowfield(scene),
  mesh: (scene: THREE.Scene) => ObjectLib.RollerCoaster(scene),
  ObjectOfInterestName: "RollerCoaster",
  ObjectOfInterestScale: 1,
  BackgroundMusic: "January",
};

const Majapahit: SceneConfiguration = {
  environmentMap: (scene: THREE.Scene) => TextureLib.SataraNight(scene),
  mesh: (scene: THREE.Scene) => ObjectLib.Majapahit(scene),
  ObjectOfInterestName: "Majapahit",
  ObjectOfInterestScale: 1,
  BackgroundMusic: "Cricket",
};


const Simulator: React.FunctionComponent<SimulatorProps> = (
  props: SimulatorProps
) => {
  const ref = React.useRef<HTMLCanvasElement>();
  const [init, setInit] = React.useState<boolean>(false);
  const [viewport, setViewport] = React.useState<Viewport>();
  const [loading, setLoading] = React.useState<boolean>(true)
  const [sceneConfig, setSceneConfig] = React.useState<SceneConfiguration>();
  const [meshReady, setMeshReady] = React.useState<boolean>(false);

  const [musicService, setMusicService] = React.useState<MusicService>();

  React.useEffect(() => {
    if (typeof ref.current == "undefined") return;
    setInit(true);
  }, [ref]);

  React.useEffect(() => {
    if (!init) return;
    ref.current?.width = window.innerWidth;
    ref.current?.height = window.innerHeight;
    const parameters: THREE.WebGLRendererParameters = {
      canvas: ref.current,
      antialias: true,
    };
    const renderer = new THREE.WebGLRenderer(parameters);
    // Tone Mapping
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.6;

    // renderer.setClearColor(0xffffff, 1.0);
    const scene = new THREE.Scene();
    const clock = new THREE.Clock();
    const camera = new THREE.PerspectiveCamera(70);
    camera.lookAt(0, 0, 0);
    camera.position.set(0, 0, 10);
    const light = new THREE.AmbientLight("white", 0.8)
    scene.add(light)
    const resizeListener = WindowResize.resizeListener(renderer, camera);
    setViewport({
      scene: scene,
      camera: camera,
      renderer: renderer,
      clock: clock,
    });
    Toast(`🖱️Click❕`);
  }, [init]);

  let loadingMusic: boolean = false;
  let lastDrawCall: number = -1;
  let lastClick = performance.now();
  window.animationFrameIndex = 0;
  let sceneConfigs: SceneConfiguration[] = [
    PassageWay,
    Majapahit,
    RollerCoaster,
    
  ];

  React.useEffect(() => {
    console.log(
      `SceneConfigState: loaded:${
        typeof sceneConfig == "undefined"
      } ViewportState: ${typeof viewport == "undefined"}`
    );
    if (typeof viewport == "undefined" || typeof sceneConfig == "undefined") return;
    // Set Loading to True
    setLoading(true)
    const { scene, camera, renderer } = viewport as Viewport;
    // Load Environment Map
    scene.background = null;
    scene.environment = null;
    setMeshReady(false);
    sceneConfig.environmentMap(scene).then(() => {
      // Remove visibility of all configured objects of interest
      sceneConfigs.forEach((curr) => {
        const currOoi = scene.getObjectByName(curr.ObjectOfInterestName);
        if (typeof currOoi != "undefined" && currOoi.visible) {
          currOoi.visible = false;
        }
      });
      const objectOfInterest = scene.getObjectByName(
        sceneConfig.ObjectOfInterestName
      );
      if (typeof objectOfInterest == "undefined") {
        sceneConfig.mesh(scene).then(() => {
          setMeshReady(true);
        });
      } else {
        objectOfInterest.visible = true;
        setMeshReady(true);
      }
    })
  }, [sceneConfig]);

  // Fine Tune Mesh Object Rigging
  React.useEffect(() => {
    console.log(
      `MeshState: ${meshReady} AnimateIndexState: ${window.animationFrameIndex}`
    );
    if (!meshReady || typeof sceneConfig == "undefined") return;
    setTimeout(() => {
      let requestId = window.animationFrameIndex;
      window.animationFrameIndex = 0;
      while (requestId--) window.cancelAnimationFrame(requestId);

      const { scene, camera } = viewport as Viewport;
      const obj = scene.getObjectByName(
        sceneConfig.ObjectOfInterestName
      ) as THREE.Object3D;
      if (typeof obj != "undefined") {
        obj.scale.set(
          sceneConfig.ObjectOfInterestScale,
          sceneConfig.ObjectOfInterestScale,
          sceneConfig.ObjectOfInterestScale
        );
      }
      // Set Loading to False
      setLoading(false)
      // Start Draw calls to GPU
      drawStep();
    }, 3000);
  }, [meshReady]);

  useEffect(() => {
    if(loading) {
      document.getElementById("canvas")?.style.visibility = "hidden"
      const elem = document.createElement("div")
      elem.id = "loading"
      elem.innerHTML = '<div class="zebraText">ROHVR</div>';
      elem.onclick = drawInteraction
      document.getElementById("app")?.appendChild(elem)
    } else {
      document.getElementById("canvas")?.style.visibility = "visible"
      document.getElementById("loading")?.remove()
    }
  }, [loading])
  
  // Draw = Update, Position and Render
  const drawStep = async () => {
    if (typeof sceneConfig == "undefined") return;
    window.animationFrameIndex = requestAnimationFrame(drawStep);
    /// Fetch instruments
    const { scene, camera, renderer, clock } = viewport as Viewport;
    // Measure time
    const elapsed = clock.getElapsedTime();
    if (lastDrawCall != -1 && elapsed - lastDrawCall < 1.0 / props.fps) return;
    // Apply animation
    const animation = ObjectLib.AnimationMaps[sceneConfig.ObjectOfInterestName];
    if (typeof animation != "undefined") {
      let dataArray: Uint8Array | undefined = undefined;
      if (typeof musicService != "undefined") {
        musicService.analyzer.getByteTimeDomainData(
          musicService.dataArray as Uint8Array
        );
        dataArray = musicService.dataArray;
      }
      animation(clock, scene, camera, dataArray);
    }
    renderer.render(scene, camera);
    lastDrawCall = elapsed;
  };

  // Music
  const injectMusic = async () => {
    if (typeof musicService == "undefined" && !loadingMusic) {
      loadingMusic = true;
      Toast("Loading...🎵");
      const musicService = await getMusic()
      setMusicService(musicService);
    } 
  };

  const drawInteraction = async () => {
    const timeDelta = Math.round(performance.now() - lastClick);
    Toast(`🐭✅`);
    // Time Block 0
    if (!loadingMusic && typeof musicService == "undefined") await injectMusic();
    // Time Block 1
    if (timeDelta < 3000) return;
    if (typeof sceneConfig == "undefined") {
      setSceneConfig(Majapahit);
      Toast(`🖌️ ${Majapahit.ObjectOfInterestName}`);
      lastClick = performance.now();
      setTimeout(() => Toast(`🖱️Click❕`), 6000);
    }
    if(typeof sceneConfig !== "undefined" && typeof musicService !== 'undefined') {
      musicService.startBgMusic(sceneConfig.BackgroundMusic);
    }    
    // Time Block 2
    if (timeDelta < 14000) return;
    if (sceneConfig?.ObjectOfInterestName == "Majapahit") {
      setSceneConfig(RollerCoaster);
      Toast(`🖌️ ${RollerCoaster.ObjectOfInterestName}`);
      lastClick = performance.now();
      setTimeout(() => Toast(`🖱️Click❕`), 6000);
    } else if (sceneConfig?.ObjectOfInterestName == "RollerCoaster") {
      setSceneConfig(PassageWay);
      Toast(`🖌️ ${PassageWay.ObjectOfInterestName}`);
      lastClick = performance.now();
      setTimeout(() => Toast(`🖱️Click❕`), 6000);
    } else if (sceneConfig?.ObjectOfInterestName == "StoneFruit") {
      setSceneConfig(Majapahit);
      Toast(`🖌️ ${Majapahit.ObjectOfInterestName}`);
      lastClick = performance.now();
      setTimeout(() => Toast(`🖱️Click❕`), 6000);
    }
  }

  return (
    <>
      <canvas
        id="canvas"
        ref={ref}
        onClick={drawInteraction}
      ></canvas>
    </>
  );
};

export default Simulator;
